/**
 * CLI: `canary application recipe` — provision + audit a property's interaction recipes
 *
 * Interaction recipe = a named, parameterized, re-entrant Playwright transaction
 * scoped to a property (an app-wide interaction mechanic — e.g. "select an item
 * from a menu that self-dismisses"). Recipes are normally minted by the browser
 * agent, but this CLI is the human surface: list/get for auditing, and
 * `upsert`/`archive` for MANUALLY provisioning a working recipe for a tricky
 * interaction the agent hasn't learned yet. Upsert runs the same server-side
 * lint as the agent tool. Thin client over /org/properties/:id/interaction-recipes.
 */

import { readFileSync } from 'node:fs';
import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest, getApplicationArg } from './cli-helpers.js';

type ArgsSchema = {
  properties?: Record<string, { type?: string } | undefined>;
  required?: unknown;
};

type RecipeListRow = {
  id: string;
  slug: string;
  title: string;
  whenToUse: string;
  tags: string[];
  argsSchema: ArgsSchema;
  currentVersion: number;
  healthState: string;
  callCount: number;
  distinctWorkflowCount: number;
  successStreak: number;
  codeBytes: number;
  lastCalledAt: string | null;
  updatedAt: string | null;
  archivedAt: string | null;
};

type Recipe = RecipeListRow & {
  code: string;
  docMarkdown: string | null;
  orgId: string;
  propertyId: string;
};

/**
 * Render a recipe's arg contract as a compact signature, e.g.
 * `menuName: string (required), itemName: string`. Empty schema → `(none)`.
 * Exported for testing.
 */
export function formatArgSignature(argsSchema: ArgsSchema): string {
  const properties = argsSchema.properties ?? {};
  const required = new Set(
    (Array.isArray(argsSchema.required) ? argsSchema.required : []).filter(
      (name): name is string => typeof name === 'string'
    )
  );
  const names = Object.keys(properties);
  if (names.length === 0) return '(none)';
  return names
    .map((name) => {
      const type = properties[name]?.type ?? 'any';
      return `${name}: ${type}${required.has(name) ? ' (required)' : ''}`;
    })
    .join(', ');
}

/**
 * Build an example `--args` JSON object with a placeholder per property, so an
 * agent gets a paste-ready call skeleton, e.g. `{"menuName":"<string>"}`.
 * Exported for testing.
 */
export function exampleArgsJson(argsSchema: ArgsSchema): string {
  const properties = argsSchema.properties ?? {};
  const example: Record<string, string> = {};
  for (const name of Object.keys(properties)) {
    example[name] = `<${properties[name]?.type ?? 'value'}>`;
  }
  return JSON.stringify(example);
}

/** Quote one argv value for safe copy/paste into a POSIX-compatible shell. */
export function shellQuote(value: string): string {
  return `'${value.replaceAll("'", `'\\''`)}'`;
}

/** The exact command that runs a recipe against a live session. */
export function callCommand(propertyId: string, slug: string, argsSchema: ArgsSchema): string {
  const hasArgs = Object.keys(argsSchema.properties ?? {}).length > 0;
  const argsPart = hasArgs ? ` --args ${shellQuote(exampleArgsJson(argsSchema))}` : '';
  return `cofactor session call-recipe --application ${shellQuote(propertyId)} --slug ${shellQuote(slug)}${argsPart}`;
}

type UpsertResult = {
  id: string;
  slug: string;
  title: string;
  currentVersion: number;
  healthState: string;
};

type ListResponse = { ok: boolean; error?: string; data?: RecipeListRow[] };
type GetResponse = { ok: boolean; error?: string; data?: Recipe };
type UpsertResponse = {
  ok: boolean;
  error?: string;
  errors?: string[];
  warnings?: string[];
  data?: UpsertResult;
};
type OkResponse = { ok: boolean; error?: string };

function printHelp(): void {
  console.log(
    [
      "canary application recipe: Provision + audit a property's interaction recipes.",
      '',
      'Interaction recipe = a named, re-entrant Playwright transaction for an app-wide',
      'interaction mechanic (e.g. a self-dismissing menu). Normally minted by the agent;',
      'these commands let you audit them and hand-provision working ones for tricky cases.',
      '',
      'Sub-commands:',
      '  list      --application <id> [--include-archived] [--format json|table]',
      '  get       --application <id> --slug <slug> [--format json|code]',
      '  upsert    --application <id> --file <recipe.json> [--base-version <n>]',
      '  archive   --application <id> --slug <slug> [--reason <text>]',
      '',
      'Running a recipe:',
      '  `list` and `get` print the exact ready-to-run command under "run:".',
      '  Run one against a live browser-testing session with:',
      "    cofactor session call-recipe --application <id> --slug <slug> --args '{...}'",
      '  (In an interactive session, `macro` is unavailable — pre-resolve dynamic',
      '  values into --args — and `attach` is a no-op. Page-driving recipes work as-is.)',
      '',
      'upsert --file expects a JSON object (argsSchema is a JSON Schema object):',
      '  {',
      '    "slug": "select-from-transient-menu",',
      '    "title": "Select an item from a self-dismissing menu",',
      '    "whenToUse": "Menus in this app unmount seconds after opening.",',
      '    "tags": ["menu", "overlay"],',
      '    "argsSchema": {',
      '      "type": "object",',
      '      "properties": { "menuName": { "type": "string" }, "itemName": { "type": "string" } },',
      '      "required": ["menuName", "itemName"]',
      '    },',
      '    "code": "async ({ page, args }) => { /* open + click atomically */ }",',
      '    "docMarkdown": "optional usage notes"',
      '  }',
      '',
      'Notes:',
      '  --property is accepted as an alias for --application.',
      '  upsert runs the same server-side lint as the agent (rejects secrets/foreign',
      '  hosts/unreferenced args). Pass --base-version <current> to EDIT an existing',
      '  recipe; omit it to create. A hand-provisioned recipe starts at health',
      '  "unverified" and earns "verified" through the normal replay ladder.',
      '  archive is a LOSSLESS prune: re-running upsert on the same slug restores it.',
    ].join('\n')
  );
}

function requirePropertyId(argv: string[]): string {
  const propertyId = getApplicationArg(argv);
  if (!propertyId) {
    console.error('Error: --application is required (alias: --property).');
    process.exit(1);
  }
  return propertyId;
}

function requireSlug(argv: string[]): string {
  const slug = getArgValue(argv, '--slug');
  if (!slug) {
    console.error('Error: --slug <slug> is required.');
    process.exit(1);
  }
  return slug;
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = requirePropertyId(argv);
  const includeArchived = hasFlag(argv, '--include-archived', '--all');
  const format = getArgValue(argv, '--format') ?? (hasFlag(argv, '--json') ? 'json' : 'table');

  const query = includeArchived ? '?includeArchived=1' : '';
  const result = await apiRequest<ListResponse>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/interaction-recipes${query}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  const rows = result.data ?? [];
  if (format === 'json') {
    console.log(JSON.stringify(rows, null, 2));
    return;
  }

  if (rows.length === 0) {
    console.log('No interaction recipes for this property.');
    return;
  }

  for (const row of rows) {
    const flag = row.archivedAt ? ' [archived]' : '';
    const reuse = `${row.callCount} calls / ${row.distinctWorkflowCount} flows`;
    console.log(
      `${row.slug}  (v${row.currentVersion}, ${row.healthState}, ${reuse}, ${row.codeBytes}B)${flag}\n` +
        `  ${row.title} — ${row.whenToUse}\n` +
        `  args: ${formatArgSignature(row.argsSchema)}` +
        (row.archivedAt
          ? '\n  run:  (archived; restore with upsert before calling)'
          : `\n  run:  ${callCommand(propertyId, row.slug, row.argsSchema)}`)
    );
  }
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = requirePropertyId(argv);
  const slug = requireSlug(argv);
  const format = getArgValue(argv, '--format') ?? (hasFlag(argv, '--json') ? 'json' : 'code');

  const result = await apiRequest<GetResponse>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/interaction-recipes/${encodeURIComponent(slug)}`
  );
  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'not found'}`);
    process.exit(1);
  }

  const recipe = result.data;
  if (format === 'json') {
    console.log(JSON.stringify(recipe, null, 2));
    return;
  }

  console.log(
    `# ${recipe.title} (${recipe.slug})\n` +
      `version: ${recipe.currentVersion}  health: ${recipe.healthState}` +
      `${recipe.archivedAt ? ' [archived]' : ''}\n` +
      `tags: ${recipe.tags.join(', ') || '(none)'}\n` +
      `whenToUse: ${recipe.whenToUse}\n` +
      `args: ${formatArgSignature(recipe.argsSchema)}\n` +
      `run:  ${callCommand(propertyId, recipe.slug, recipe.argsSchema)}\n\n` +
      `${recipe.code}` +
      (recipe.docMarkdown ? `\n\n---\n${recipe.docMarkdown}` : '')
  );
}

async function handleUpsert(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = requirePropertyId(argv);
  const file = getArgValue(argv, '--file');
  if (!file) {
    console.error('Error: --file <recipe.json> is required. See `recipe help` for the shape.');
    process.exit(1);
  }

  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(readFileSync(file, 'utf8')) as Record<string, unknown>;
  } catch (error) {
    console.error(
      `Error reading ${file}: ${error instanceof Error ? error.message : String(error)}`
    );
    process.exit(1);
  }

  const baseVersionArg = getArgValue(argv, '--base-version');
  if (baseVersionArg !== undefined) {
    const parsed = Number(baseVersionArg);
    if (!Number.isInteger(parsed) || parsed < 1) {
      console.error('Error: --base-version must be a positive integer.');
      process.exit(1);
    }
    payload.baseVersion = parsed;
  }

  const result = await apiRequest<UpsertResponse>(
    apiUrl,
    token,
    'POST',
    `/org/properties/${propertyId}/interaction-recipes`,
    payload
  );

  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'upsert failed'}`);
    if (result.errors?.length) {
      console.error('Lint errors:');
      for (const err of result.errors) console.error(`  - ${err}`);
    }
    process.exit(1);
  }

  const saved = result.data;
  console.log(
    `Provisioned recipe "${saved.slug}" v${saved.currentVersion} (health: ${saved.healthState}).`
  );
  if (result.warnings?.length) {
    console.log('Lint warnings:');
    for (const warn of result.warnings) console.log(`  - ${warn}`);
  }
}

async function handleArchive(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = requirePropertyId(argv);
  const slug = requireSlug(argv);
  const reason = getArgValue(argv, '--reason');

  const result = await apiRequest<OkResponse>(
    apiUrl,
    token,
    'POST',
    `/org/properties/${propertyId}/interaction-recipes/${encodeURIComponent(slug)}/archive`,
    reason ? { reason } : {}
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  console.log(
    `Archived interaction recipe "${slug}" (restorable — re-run upsert to bring it back).`
  );
}

export async function runRecipe(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [action, ...rest] = argv;

  if (!action || action === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  switch (action) {
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'get':
      await handleGet(rest, apiUrl, token);
      break;
    case 'upsert':
      await handleUpsert(rest, apiUrl, token);
      break;
    case 'archive':
      await handleArchive(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown recipe action: ${action}`);
      printHelp();
      process.exit(1);
  }
}
